import { Box, Grid, Typography } from "@mui/material";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { InfoCard } from "src/design";
import {
  colorAccent,
  colorBorder,
  colorSurface,
  colorTextDisabled,
  colorTextMuted,
  colorTextPrimary,
  radiusInput,
} from "src/design/tokens";

interface BookingDurationSectionProps {
  bookingDuration: number;
  disabled: boolean;
  onChange: (value: number) => void;
}

const DURATION_OPTIONS: { value: number; labelKey: string }[] = [
  { value: 60, labelKey: "schedule.duration1h" },
  { value: 90, labelKey: "schedule.duration1h30" },
  { value: 120, labelKey: "schedule.duration2h" },
];

export const BookingDurationSection = ({
  bookingDuration,
  disabled,
  onChange,
}: BookingDurationSectionProps) => {
  const { t } = useTranslation();
  const [duration, setDuration] = useState(bookingDuration);

  useEffect(() => {
    setDuration(bookingDuration);
  }, [bookingDuration]);

  const handleSelect = (value: number) => {
    if (disabled || value === duration) return;
    setDuration(value);
    onChange(value);
  };

  return (
    <Grid item>
      <InfoCard
        title={t("schedule.bookingDuration")}
        subtitle={t("schedule.bookingDurationSub")}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1.5,
            py: 1.5,
          }}
        >
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600, color: colorTextPrimary }}>
              {t("schedule.reservationDuration")}
            </Typography>
            <Typography variant="caption" sx={{ color: colorTextMuted }}>
              {t("schedule.reservationDurationDesc")}
            </Typography>
          </Box>
          <Box
            sx={{
              display: "flex",
              borderRadius: radiusInput,
              border: `1px solid ${colorBorder}`,
              overflow: "hidden",
              opacity: disabled ? 0.5 : 1,
              pointerEvents: disabled ? "none" : "auto",
            }}
          >
            {DURATION_OPTIONS.map((option, index) => {
              const isSelected = duration === option.value;
              return (
                <Box
                  key={option.value}
                  onClick={() => handleSelect(option.value)}
                  sx={{
                    px: { xs: 1.5, sm: 2 },
                    py: 0.75,
                    cursor: disabled ? "default" : "pointer",
                    backgroundColor: isSelected ? colorAccent : colorSurface,
                    color: isSelected ? "#ffffff" : colorTextDisabled,
                    fontWeight: isSelected ? 600 : 400,
                    fontSize: "0.8125rem",
                    lineHeight: 1.5,
                    userSelect: "none",
                    transition: "background-color 0.15s ease, color 0.15s ease",
                    borderLeft: index > 0 ? `1px solid ${colorBorder}` : "none",
                    "&:hover": disabled
                      ? {}
                      : {
                          backgroundColor: isSelected ? colorAccent : "#f9fafb",
                        },
                  }}
                >
                  {t(option.labelKey)}
                </Box>
              );
            })}
          </Box>
        </Box>
      </InfoCard>
    </Grid>
  );
};
